import { useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router'
import type { JSX, SyntheticEvent } from 'react'
import { api, call } from '@/api/client'
import type { ApiFailure } from '@/api/client'
import { useMutation } from '@/api/query'
import { useAuth } from '@/auth/context'
import { followHandoff } from '@/auth/handoff'
import { IDLE_TIMEOUT_MINUTES } from '@/auth/idle'
import { useBrand } from '@/brand/context'
import { keepSignedIn, rememberKeepSignedIn } from '@/auth/persistence'
import { DEFAULT_HOME, NEXT_PARAM, isApexBandRoute, safeNext } from '@/auth/routes'
import { looksLikeMobile } from '@/lib/format'
import { Button } from '@/ui/Button'
import { TextField } from '@/ui/Field'
import { GoogleButton } from '@/ui/GoogleButton'
import { FullPageLoading, InlineError } from '@/ui/states'
import { PublicPage } from './PublicPage'

/** Mirrors `MAX_FAILED_LOGINS` in `app/security/lockout.py`. Stated, never counted — see below. */
const ATTEMPTS_BEFORE_LOCK = 5

/**
 * `/login` — S-D2 screens 03, 04 and 12.
 *
 * **The attempt counter S-D2 drew is deliberately not rendered, and the reason
 * is the design's own.** Screen 04 says "2 attempts left"; screen 04's rationale
 * says the message must never confirm that an account exists. Those two cannot
 * both hold: a counter is only available for a real account, so showing one for
 * `priya@…` and not for `nobody@…` answers exactly the question the generic
 * message refuses to. What survives is the *goal* — "a real user knows a lockout
 * is coming rather than being surprised by it" — met by stating the policy,
 * which is identical for every response and therefore tells an attacker nothing.
 *
 * `ACCOUNT_LOCKED` is screen 12, with the wait read from the `Retry-After`
 * header the API already sends. That answer *is* only produced for a real
 * account — but only after the attacker has locked it themselves, so it reveals
 * nothing they did not just cause.
 */
export function Login(): JSX.Element {
  const navigate = useNavigate()
  const { adopt, signedOutReason } = useAuth()
  const brand = useBrand()
  const [params] = useSearchParams()
  // Where this sign-in was interrupted on the way to, when it was. Kept
  // apart from the default so a handoff is not told to go to `/dashboard`
  // it was going to anyway.
  const asked = safeNext(params.get(NEXT_PARAM))
  const next = asked ?? DEFAULT_HOME
  const oauthFailed = params.get('error') === 'google'
  // `Handoff` sends every failure here with one word: the token was expired,
  // spent, or minted for another host, and the remedy is the same for all.
  const handoffFailed = params.get('error') === 'handoff'
  // Whose sign-in page this is. On an organisation's own host there is no
  // trial to start and no other organisation to land in; and until the signup
  // email that claimed the workspace is confirmed, the host refuses everybody
  // (`WORKSPACE_UNVERIFIED`), so the form says so instead of collecting a
  // password it cannot use.
  const tenantHost = brand.host === 'tenant'
  const unverified = tenantHost && !brand.verified

  const [identifier, setIdentifier] = useState('')
  const [password, setPassword] = useState('')
  const [lockedFor, setLockedFor] = useState<number | null>(null)
  // The browser has been told to go to the organisation's host. Held as state
  // so the form does not come back to life for the frame between the
  // navigation being asked for and the page unloading.
  const [leaving, setLeaving] = useState(false)
  // Seeded from the last choice made in this browser, not from `true`, so the
  // box a person unticked on a shared machine is still unticked next time.
  const [persistent, setPersistent] = useState(keepSignedIn)

  const login = useMutation(
    (body: { email?: string; mobile?: string; password: string; persistent: boolean }) =>
      call(() => api.POST('/api/v1/auth/login', { body })),
  )

  // AUTH-6: screen 04 marks both fields invalid and says nothing under either.
  // Only for the credentials refusal — a lockout or a rate limit is not a
  // statement about what is in the boxes, and outlining them would say it was.
  const credentialsWrong = login.state.failure?.code === 'INVALID_CREDENTIALS'

  async function onSubmit(event: SyntheticEvent): Promise<void> {
    event.preventDefault()
    const trimmed = identifier.trim()
    // Written before the request rather than after a success: the choice is
    // about this browser, and it is just as true when the password was wrong.
    rememberKeepSignedIn(persistent)
    const result = await login.run(
      looksLikeMobile(trimmed)
        ? { mobile: trimmed, password, persistent }
        : { email: trimmed.toLowerCase(), password, persistent },
    )
    if (result.ok) {
      // On the apex the answer may name the organisation's host to continue
      // on (spec: *Sessions and the handoff*). That is a different origin, so
      // it is a full navigation — and the identity is not adopted first, since
      // `RequireAnonymous` would then redirect through the organisation picker
      // for a frame before the browser leaves.
      //
      // Unless the sign-in was interrupted on the way to a page that lives on
      // the apex itself — an invitation being accepted, the picker — in which
      // case that page is the destination and the handoff is not taken: the
      // organisation's host cannot show it, and the invitation would be lost
      // on the way. Any other `next` is a page on the organisation's host,
      // and rides across with the handoff.
      const stayHere = asked !== null && isApexBandRoute(asked)
      if (!stayHere && followHandoff(result.data.handoff_url, asked ?? undefined)) {
        setLeaving(true)
        return
      }
      adopt({ user: result.data.user, session: result.data.session })
      void navigate(next, { replace: true })
      return
    }
    setLockedFor(result.failure.code === 'ACCOUNT_LOCKED' ? lockMinutes(result.failure) : null)
  }

  if (lockedFor !== null) return <Locked minutes={lockedFor} />
  if (leaving) return <FullPageLoading label="Opening your workspace" />

  return (
    <PublicPage
      title="Welcome back"
      footer={
        // No trial link on a tenant's host: signing up is the apex's, and a
        // white-labelled page does not invite strangers into someone else's
        // organisation (spec: *Public auth screens*).
        tenantHost ? undefined : (
          <>
            New here?{' '}
            {/* AUTH-37: `.ar-link` explicitly. The bare `<a>` safety net in
                `app.css` matches the colour and the underline offset but not
                `.ar-link`'s hover thickness, so the link was visibly inert on
                hover where every other link in the product is not. */}
            <Link className="ar-link" to={`/signup?${NEXT_PARAM}=${encodeURIComponent(next)}`}>
              Start a free trial
            </Link>
          </>
        )
      }
    >
      {/*
        `method="post"` is never used while the bundle is running — `onSubmit` calls
        `preventDefault` — and it is here for the case where the bundle is *not*
        running. A `<form>` with no method defaults to GET, so a submit with dead
        JavaScript would serialise this password into the query string, where it
        reaches the CloudFront access log, the browser history and any `Referer`.
        Two attributes against a class of leak that is unfixable after the fact.

        Not hypothetical: on 2026-07-30 a CSP directive was found to have been
        silently breaking hydration on augrank.com for the life of that site, which
        is exactly the precondition this guards.
      */}
      <form
        method="post"
        onSubmit={(event) => {
          void onSubmit(event)
        }}
      >
        {/* `fieldset` rather than `disabled` on each control: an unverified
            workspace disables the *form*, and a fieldset is the element HTML
            gives that statement — every field and the submit inside it go
            inert together, and a control added later goes with them. */}
        <fieldset className="app-fieldset" disabled={unverified}>
          <div className="ar-card__body ar-stack">
            {unverified ? (
              <div className="ar-alert ar-alert--info" role="status">
                <span className="ar-alert__icon" aria-hidden="true">
                  ✉
                </span>
                <div className="ar-alert__body">
                  <strong>Verify your email to open this workspace.</strong>
                  Check the message we sent when you signed up.
                </div>
              </div>
            ) : (
              <GoogleButton
                label="Continue with Google"
                {...(tenantHost && brand.workspace ? { workspace: brand.workspace } : {})}
              />
            )}

            {signedOutReason === 'idle' ? (
              // `role="status"`, not `alert`: nothing went wrong, and the person
              // reading it is the one who walked away. It says the number so the
              // sign-out reads as policy rather than a fault, and `next` above
              // still carries them back to the screen they left.
              <div className="ar-alert ar-alert--info" role="status">
                <span className="ar-alert__icon" aria-hidden="true">
                  ◷
                </span>
                <div className="ar-alert__body">
                  <strong>You were signed out</strong>
                  For your security we sign you out after {IDLE_TIMEOUT_MINUTES} minutes without
                  activity. Sign in again to pick up where you left off.
                </div>
              </div>
            ) : null}

            {handoffFailed ? (
              <div className="ar-alert ar-alert--danger" role="alert">
                <span className="ar-alert__icon" aria-hidden="true">
                  ✕
                </span>
                <div className="ar-alert__body">
                  <strong>That sign-in link has expired.</strong>
                  Sign in again.
                </div>
              </div>
            ) : null}

            {oauthFailed ? (
              <div className="ar-alert ar-alert--danger" role="alert">
                {/* AUTH-36: `✕` is the design's refusal glyph. See `ui/Field.tsx`. */}
                <span className="ar-alert__icon" aria-hidden="true">
                  ✕
                </span>
                <div className="ar-alert__body">
                  <strong>We could not sign you in with Google</strong>
                  Try again, or use your email address and password.
                </div>
              </div>
            ) : null}

            {/* AUTH-4: focus lands on the summary, so the refusal is where the
              keyboard is rather than three controls above it. */}
            <InlineError failure={login.state.failure} takesFocus />

            {/* AUTH-42. "Email or mobile", and no help line. Screen 03 gives
              this field a label and nothing else, and the help it carried was
              instructions for one of the two things it accepts — a sentence
              about international dialling under a box most people are about to
              type an email address into. The form telling somebody how to
              write a mobile number belongs on the screen that only takes one,
              which is signup's mobile route, where it still is. */}
            <TextField
              label="Email or mobile"
              type="text"
              required
              autoFocus
              autoComplete="username"
              inputMode="email"
              invalid={credentialsWrong}
              value={identifier}
              onChange={(event) => {
                setIdentifier(event.target.value)
                login.reset()
              }}
            />
            <TextField
              label="Password"
              type="password"
              required
              autoComplete="current-password"
              invalid={credentialsWrong}
              value={password}
              onChange={(event) => {
                setPassword(event.target.value)
                login.reset()
              }}
            />
            <div className="ar-row" style={{ justifyContent: 'space-between' }}>
              {/* AUTH-5. Ticked by default, as screen 03 draws it, and what it
                controls is the *cookie's* lifetime — unticked asks for one that
                is discarded when the browser closes. The server's idle and
                absolute timeouts are unchanged either way, so this can only
                make a session shorter (`app/security/sessions.set_cookie`).

                AUTH-44: body-sm and flush, so the box aligns with the row's
                left edge and the two ends of the row read at the same size. */}
              <label className="ar-choice ar-choice--check app-choice--sm app-choice--flush">
                <input
                  type="checkbox"
                  checked={persistent}
                  onChange={(event) => {
                    setPersistent(event.target.checked)
                  }}
                />
                <span className="ar-choice__box" aria-hidden="true" />
                <span className="ar-choice__text">Keep me signed in</span>
              </label>
              {/* AUTH-46. On screen 03 the reset link sits here; on screen 04 —
                once the credentials have been refused — it moves below the
                primary action and becomes the screen's closing line. That is
                not decoration: before a failure it is one of two things you
                might want from this row, and after one it is the way out, so
                it stops competing with "Keep me signed in" for the same
                glance. AUTH-43 sets its size and its wording. */}
              {credentialsWrong ? null : (
                <Link
                  className="ar-link"
                  style={{ fontSize: 'var(--ar-text-body-sm-size)' }}
                  to="/forgot-password"
                >
                  Forgot password?
                </Link>
              )}
            </div>
            {/* The policy, not a counter — see the note on this component. It
              moved out of the row above when the checkbox took that side, and
              it is a line of its own rather than help text on a field because
              it is about the account, not about either input. */}
            <span className="ar-subtle" style={{ fontSize: 'var(--ar-text-body-sm-size)' }}>
              {ATTEMPTS_BEFORE_LOCK} wrong attempts locks the account for a few minutes.
            </span>
            {/* AUTH-34. The primary action is the last child of the card body, in
              the stack with everything above it. It was in an `.ar-card__footer`,
              which draws a hairline rule across the card and pads it separately
              — a footer is for a card whose body is a *subject* and whose
              actions are about it, and none of the eleven auth screens is
              shaped that way. `ar-card__footer` appears nowhere in the whole
              of `design/screens/auth.html`. */}
            <Button
              tone="primary"
              size="lg"
              block
              type="submit"
              busy={login.pending}
              busyLabel="Signing in"
              disabled={unverified}
            >
              Sign in
            </Button>
            {credentialsWrong ? (
              <p className="app-public__cardfoot">
                <Link className="ar-link" to="/forgot-password">
                  Reset your password
                </Link>
              </p>
            ) : null}
          </div>
        </fieldset>
      </form>
    </PublicPage>
  )
}

/**
 * How long the lock has left, from the header the API already sends.
 *
 * Rounded up, and floored at one: "try again in 0 minutes" reads as a bug, and a
 * lock with 20 seconds left is still a lock.
 */
function lockMinutes(failure: ApiFailure): number {
  return Math.max(1, Math.ceil((failure.retryAfterSeconds ?? 0) / 60))
}

/** S-D2 screen 12. Resetting the password unlocks immediately — the API ends every session. */
function Locked({ minutes }: { minutes: number }): JSX.Element {
  return (
    <PublicPage
      /*
        AUTH-74. No `h1` above the card. Screen 12 titles itself *inside* the
        error state — "Account temporarily locked" — and the app printed that
        line above the card as well and then opened the state with a second,
        different title ("Too many failed attempts"), so one screen carried two
        headings and neither was the whole message. The state's own title is
        the page's title, and it is the `h1`, so the document still starts at
        level 1 with nothing else claiming to be the top of it.
      */
      /*
        AUTH-24. Screen 12's footer reads "If this wasn't you, resetting your
        password is the fastest way to secure the account. We've emailed the
        account owner." — and **only the first sentence is printed**, because
        the second one is not true. `_attempt_login` writes the counter and the
        lock and sends nothing (`app/v1/auth.py`); there is no lockout mail and
        no notification event for one. Telling somebody a warning is in their
        inbox when none was sent is worse than saying nothing: they go and look,
        find nothing, and conclude the lock was not real.

        If a lockout notice is added later, this is the sentence that goes with
        it — not before.
      */
      footer="If this wasn't you, resetting your password is the fastest way to secure the account."
    >
      <div className="ar-card__body">
        <div className="ar-error-state" role="alert">
          <div className="ar-error-state__icon" aria-hidden="true">
            !
          </div>
          <h1 className="ar-error-state__title">Account temporarily locked</h1>
          {/* AUTH-75. A `<span>`, not a `<p>`: `.ar-error-state` is a grid with
              its own `gap`, and a `<p>` adds the user agent's `margin-block`
              on top of it at both ends of the row — margins do not collapse
              inside a grid. The remaining `<p>` elements in `ui/states.tsx`
              are handled by the reset in `app.css` rather than by a sweep,
              because two of these titles are page headings and an `<h1>`
              carries the same margin.

              AUTH-74 also restores the design's opening clause: the state used
              to lead with "Too many failed attempts" as its title and then
              start the body at "You can try again", which reads as an answer
              to a question the screen never asked. */}
          <span className="ar-error-state__body">
            Too many failed attempts. You can try again in about <strong>{minutes} minutes</strong>,
            or reset your password now to unlock immediately.
          </span>
          <div className="ar-btn-group" style={{ justifyContent: 'center' }}>
            <Link className="ar-btn ar-btn--primary ar-btn--sm" to="/forgot-password">
              Reset password
            </Link>
            <a className="ar-btn ar-btn--ghost ar-btn--sm" href="mailto:support@augrank.com">
              Contact support
            </a>
          </div>
        </div>
      </div>
    </PublicPage>
  )
}
